import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import {
  ChartIcons,
  HighlightsIcons,
  InfoIcons,
  NotebookIcons,
  QuestionIcons,
} from '../../../../../../../icons/icons';
import { Drawer, NameEmailAvatar } from '../../../../../../../ui';
import { PencilIcon } from '../../../../../../../ui/icons/icons';
import { getInterviewRound } from '../../../../../../../utils/api_service/SmartInterview';
import ScoreStatus from '../Components/ScoreStatus';
import { IInterviewResult } from '../Hooks';
import { Highlights } from './Tabs/Highlights';
import { Notes } from './Tabs/Notes';
import { QA } from './Tabs/QnA';
import { Transcripts } from './Tabs/Transcripts ';

export interface IUserDetails {
  isOpen: boolean;
  onCloseDrawer: () => void;
  result: IInterviewResult | null;
}

export const UserDetails: React.FC<IUserDetails> = ({
  isOpen,
  onCloseDrawer,
  result,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const [activeTab, setActiveTab] = useState('overallScore');
  const [activeContentTab, setActiveContentTab] = useState('highlights');
  const [panelistsMap, setPanelistsMap] = useState<Record<string, any>>({});

  // Fetch panelists info when drawer opens
  useEffect(() => {
    if (!isOpen || !result?.interviewRoundId) return;

    const fetchPanelistsInfo = async () => {
      try {
        const response = await getInterviewRound(result.interviewRoundId);
        if (!response.ok) throw new Error(`Status ${response.status}`);
        const round = await response.json();

        const panelists: Record<string, any> = {};
        const panelistsArray = round?.panelists || [];
        panelistsArray.forEach((p: any) => {
          if (p?.user?.id) {
            panelists[p.id] = {
              firstName: p.user.firstName,
              lastName: p.user.lastName,
              email: p.user.email,
              avatar: p.user.profilePic || '',
            };
          }
        });

        setPanelistsMap(panelists);
      } catch (err) {
        setPanelistsMap({});
      }
    };

    fetchPanelistsInfo();
  }, [isOpen, result?.interviewRoundId]);

  // Prepare scores data
  const noJustification = t(SI.results.userDetails.noJustification);
  const scores = result
    ? [
      {
        key: 'overallScore',
        label: t(SI.results.userDetails.scores.overall),
        value: `${result.aiOverallScore?.score ?? 0}%`,
        analysis: t(SI.results.userDetails.aiAnalysis),
      },
      {
        key: 'technicalSkills',
        label: t(SI.results.userDetails.scores.technical),
        value: `${result.technicalSkills?.score ?? 0}%`,
        analysis: result.technicalSkills?.justification || noJustification,
      },
      {
        key: 'roleExperience',
        label: t(SI.results.userDetails.scores.roleExperience),
        value: `${result.roleExperience?.score ?? 0}%`,
        analysis: result.roleExperience?.justification || noJustification,
      },
      {
        key: 'industrySkills',
        label: t(SI.results.userDetails.scores.industrySkills),
        value: `${result.industrySkills?.score ?? 0}%`,
        analysis: result.industrySkills?.justification || noJustification,
      },
      {
        key: 'productKnowledge',
        label: t(SI.results.userDetails.scores.productKnowledge),
        value: `${result.productKnowledge?.score ?? 0}%`,
        analysis: result.productKnowledge?.justification || noJustification,
      },
      {
        key: 'communication',
        label: t(SI.results.userDetails.scores.communication),
        value: `${result.communication?.score ?? 0}%`,
        analysis: result.communication?.justification || noJustification,
      },
      {
        key: 'problemSolving',
        label: t(SI.results.userDetails.scores.problemSolving),
        value: `${result.problemSolving?.score ?? 0}%`,
        analysis: result.problemSolving?.justification || noJustification,
      },
      {
        key: 'leadership',
        label: t(SI.results.userDetails.scores.leadership),
        value: `${result.leadership?.score ?? 0}%`,
        analysis: result.leadership?.justification || noJustification,
      },
      {
        key: 'teamCollaboration',
        label: t(SI.results.userDetails.scores.teamCollaboration),
        value: `${result.teamCollaboration?.score ?? 0}%`,
        analysis: result.teamCollaboration?.justification || noJustification,
      },
    ]
    : [];

  const activeScore = scores.find((score) => score.key === activeTab);

  return (
    <Drawer
      title={`${result?.user?.firstName || ''} ${result?.user?.lastName || ''}`}
      isOpen={isOpen}
      onClose={onCloseDrawer}
      width='w-full md:w-[900px]'
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, p: 3 }}>
        {/* Left Side: Score Type Tabs */}
        <Box sx={{ width: { xs: '100%', lg: '33.333%' } }}>
          <Box sx={{ position: 'sticky', top: 0 }}>
            <Typography
              variant='caption'
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 1,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {t(SI.results.userDetails.evaluationMetrics)}
            </Typography>
            <Stack spacing={1}>
              {scores.map((score, idx) => {
                const scoreValue = Number(
                  score.value.toString().replace('%', ''),
                );
                const isActive = activeTab === score.key;

                let scoreColor = '#6b7280';
                if (scoreValue >= 80) scoreColor = '#16a34a';
                else if (scoreValue >= 60) scoreColor = '#2563eb';
                else if (scoreValue >= 40) scoreColor = '#ea580c';
                else scoreColor = '#dc2626';

                return (
                  <Button
                    key={idx}
                    fullWidth
                    onClick={() => setActiveTab(score.key)}
                    variant={isActive ? 'contained' : 'outlined'}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderRadius: 2,
                      textTransform: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderColor: isActive ? 'primary.main' : 'divider',
                      bgcolor: isActive ? 'primary.main' : 'background.paper',
                      color: isActive ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        bgcolor: isActive ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 500,
                        textAlign: 'left',
                        color: 'inherit',
                      }}
                    >
                      {score.label}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 700,
                        color: isActive ? 'inherit' : scoreColor,
                      }}
                    >
                      {score.value}
                    </Typography>
                  </Button>
                );
              })}
            </Stack>
          </Box>
        </Box>

        {/* Right Side: Score Details */}
        <Box sx={{ width: { xs: '100%', lg: '66.666%' } }}>
          {activeScore && (
            <Stack spacing={3}>
              {/* User Information Card */}
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'rgba(34, 197, 94, 0.05)',
                  borderColor: 'rgba(34, 197, 94, 0.2)',
                }}
              >
                <NameEmailAvatar
                  avatar={''}
                  avatarHeight='80px'
                  avatarWidth='80px'
                  email={result?.user?.email || ''}
                  name={`${result?.user?.firstName || ''} ${
                    result?.user?.lastName || ''
                  }`}
                />
              </Paper>

              {/* Score Details Card */}
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        background: 'linear-gradient(to bottom right, #22c55e, #059669)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        boxShadow: 2,
                      }}
                    >
                      <ChartIcons color='#ffffff' />
                    </Box>
                    <Box>
                      <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                        {activeScore.label}
                      </Typography>
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {t(SI.results.userDetails.aiAnalysis)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <ScoreStatus
                      score={Number(
                        activeScore.value.toString().replace('%', ''),
                      )}
                    />
                  </Box>
                </Box>

                {/* Analysis Section */}
                <Paper
                  variant='outlined'
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flexShrink: 0, mt: 0.5 }}>
                    <InfoIcons color='#16a34a' />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      {t(SI.results.userDetails.assessmentJustification)}
                    </Typography>
                    <Typography variant='body2' sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      {activeScore.analysis}
                    </Typography>
                  </Box>
                </Paper>
              </Paper>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Content Tabs Section */}
      <Box sx={{ mt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', zIndex: 10 }}>
          <Tabs
            value={activeContentTab}
            onChange={(_, val) => setActiveContentTab(val)}
            variant='scrollable'
            scrollButtons='auto'
            sx={{ px: 3 }}
          >
            <Tab
              value='highlights'
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HighlightsIcons size={20} />
                  {t(SI.results.userDetails.tabs.highlights)}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              value='transcripts'
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotebookIcons size={20} />
                  {t(SI.results.userDetails.tabs.transcripts)}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              value='q&a'
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QuestionIcons size={20} />
                  {t(SI.results.userDetails.tabs.qa)}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              value='notes'
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PencilIcon size={20} />
                  {t(SI.results.userDetails.tabs.panelistNotes)}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {activeContentTab === 'highlights' && (
            <Highlights data={result?.summary || []} />
          )}
          {activeContentTab === 'transcripts' && (
            <Transcripts data={result?.transcript || []} />
          )}
          {activeContentTab === 'q&a' && (
            <QA data={result?.questionAndAnswer || []} />
          )}
          {activeContentTab === 'notes' && (
            <Notes
              data={result?.panelistNotes || []}
              panelistsMap={panelistsMap}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default UserDetails;
